import React from "react";
import styled, { keyframes } from "styled-components";
import { Link } from "react-router-dom";
import aboutHeroBg from "../images/hero_bg.webp";

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Wrapper = styled.div`
  min-height: 80vh;
  background-color: var(--darkBG);
  color: var(--lightText);
  font-family: 'Outfit', sans-serif;
  overflow-x: hidden;
`;

const HeroSection = styled.section`
  position: relative;
  width: 100%;
  padding: calc(var(--headerHeight) + 60px) 16px 80px;
  overflow: hidden;
  text-align: center;
  background: linear-gradient(180deg, rgba(10, 10, 10, 0.4) 0%, rgba(15, 15, 15, 0.8) 100%), 
              url(${aboutHeroBg}) no-repeat center center/cover;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at center, rgba(212, 175, 55, 0.15) 0%, transparent 70%);
    pointer-events: none;
    z-index: 1;
    filter: blur(50px);
  }

  .heroContent {
    position: relative;
    z-index: 2;
    max-width: 960px;
    animation: ${fadeInUp} 0.8s ease-out;
  }
`;

const HeroTitle = styled.h1`
  font-size: clamp(2.5rem, 5vw, 4.2rem);
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 2px;
  line-height: 1.1;
  margin: 0 0 20px 0;
  background: linear-gradient(135deg, #ffffff 30%, var(--accentGold) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
`;

const HeroSubtitle = styled.p`
  font-size: clamp(1rem, 2vw, 1.35rem);
  color: #cfc9be;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 3px;
  margin: 0;
  line-height: 1.5;
`;

const Container = styled.div`
  max-width: 1200px;
  width: 90%;
  margin: 0 auto;
`;

const Section = styled.section`
  padding: 80px 0;
  position: relative;
  z-index: 2;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  &:last-of-type {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 4vw, 2.6rem);
  text-align: center;
  color: #ffffff;
  margin-bottom: 50px;
  position: relative;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 2px;

  &::after {
    content: "";
    display: block;
    width: 80px;
    height: 3px;
    background: var(--accentGold);
    margin: 14px auto 0;
    border-radius: 2px;
    box-shadow: 0 0 10px rgba(212, 175, 55, 0.5);
  }
`;

const OverviewGrid = styled.div`
  max-width: 850px;
  margin: 0 auto;
  width: 100%;
`;

const OverviewText = styled.div`
  font-size: 1.15rem;
  line-height: 1.85;
  color: #cfc9be;
  
  p {
    margin: 0 0 20px 0;
    &:last-child {
      margin-bottom: 0;
    }
  }

  span.highlight {
    color: var(--accentGold);
    font-weight: 600;
  }
`;

const Timeline = styled.div`
  position: relative;
  max-width: 850px;
  margin: 0 auto;
  padding: 20px 0;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 2px;
    background: linear-gradient(180deg, transparent, rgba(212, 175, 55, 0.4) 15%, rgba(212, 175, 55, 0.4) 85%, transparent);
    transform: translateX(-50%);
  }

  @media (max-width: 768px) {
    &::before {
      left: 30px;
    }
  }
`;

const TimelineNode = styled.div`
  position: relative;
  margin-bottom: 50px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  &:nth-child(odd) {
    flex-direction: row-reverse;
  }

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    flex-direction: row !important;
    justify-content: flex-start;
    padding-left: 60px;
  }
`;

const TimelineDot = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--accentGold);
  border: 4px solid var(--darkBG);
  box-shadow: 0 0 15px var(--accentGold);
  z-index: 2;

  @media (max-width: 768px) {
    left: 30px;
  }
`;

const TimelineContent = styled.div`
  width: 44%;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(212, 175, 55, 0.15);
  padding: 28px;
  border-radius: 20px;
  transition: all 0.3s ease;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);

  &:hover {
    border-color: var(--accentGold);
    box-shadow: 0 15px 35px rgba(212, 175, 55, 0.12);
    transform: translateY(-3px);
  }

  h3 {
    color: var(--accentGold);
    font-size: 1.45rem;
    margin: 0 0 10px 0;
    font-weight: 700;
    letter-spacing: 0.5px;
  }

  p {
    color: #cfc9be;
    line-height: 1.65;
    font-size: 0.98rem;
    margin: 0;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const StrengthsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 30px;
`;

const StrengthCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1.5px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  padding: 30px;
  display: flex;
  gap: 20px;
  transition: all 0.4s ease;

  svg {
    width: 44px;
    height: 44px;
    fill: var(--accentGold);
    flex-shrink: 0;
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  h3 {
    font-size: 1.25rem;
    color: #ffffff;
    font-weight: 700;
    margin: 0;
  }

  p {
    font-size: 0.95rem;
    color: #a1a1aa;
    line-height: 1.6;
    margin: 0;
  }

  &:hover {
    transform: translateY(-5px);
    border-color: var(--accentGold);
    box-shadow: 0 12px 30px rgba(212, 175, 55, 0.12);
  }
`;

const CompanyGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 30px;
  width: 100%;
`;

const CompanyCard = styled(Link)`
  background: linear-gradient(135deg, rgba(20, 18, 16, 0.5) 0%, rgba(18, 16, 14, 0.35) 100%);
  border: 1.5px solid rgba(212, 175, 55, 0.2);
  border-radius: 24px;
  padding: 36px 30px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  width: calc(33.333% - 20px);
  min-width: 320px;
  max-width: 360px;

  h3 {
    color: var(--accentGold);
    font-size: 1.5rem;
    font-weight: 750;
    margin: 0;
    letter-spacing: 0.5px;
  }

  p {
    color: #cfc9be;
    font-size: 0.98rem;
    line-height: 1.65;
    margin: 0;
    flex-grow: 1;
  }

  .action-text {
    color: var(--accentGold);
    font-size: 0.88rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1.5px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 10px;
    transition: all 0.3s ease;

    &::after {
      content: "→";
      display: inline-block;
      transition: transform 0.3s ease;
    }
  }

  &:hover {
    transform: translateY(-8px);
    border-color: rgba(212, 175, 55, 0.4);
    box-shadow: 0 20px 45px rgba(0, 0, 0, 0.35), 0 0 40px rgba(212, 175, 55, 0.1);

    .action-text {
      color: #ffffff;
      &::after {
        transform: translateX(5px);
      }
    }
  }

  @media (max-width: 968px) {
    width: 100%;
    max-width: 100%;
  }
`;

const IndustryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 968px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const IndustryCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 20px;
  border-left: 4px solid var(--accentGold);
  transition: all 0.3s ease;

  span {
    font-size: 1.1rem;
    font-weight: 600;
    color: #ffffff;
    letter-spacing: 0.5px;
  }

  svg {
    width: 36px;
    height: 36px;
    fill: var(--accentGold);
    flex-shrink: 0;
  }

  &:hover {
    background: rgba(212, 175, 55, 0.05);
    transform: translateX(6px);
    border-color: rgba(212, 175, 55, 0.2);
  }
`;

const WhyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  max-width: 960px;
  margin: 0 auto;

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`;

const WhyItem = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  padding: 20px 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.3s ease;

  .check {
    color: var(--accentGold);
    font-weight: bold;
    font-size: 1.4rem;
    flex-shrink: 0;
  }

  span {
    font-size: 1.1rem;
    color: #cfc9be;
    font-weight: 500;
  }

  &:hover {
    border-color: rgba(212, 175, 55, 0.2);
    background: rgba(212, 175, 55, 0.03);
    transform: translateY(-2px);
  }
`;

const CTAWrapper = styled.div`
  background: linear-gradient(135deg, rgba(212, 175, 55, 0.12) 0%, rgba(18, 16, 14, 0.8) 100%);
  border: 1px solid rgba(212, 175, 55, 0.25);
  border-radius: 28px;
  padding: 60px 40px;
  text-align: center;
  max-width: 900px;
  margin: 40px auto 0;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5), 0 0 40px rgba(212, 175, 55, 0.05);

  h3 {
    font-size: clamp(1.8rem, 3.5vw, 2.5rem);
    color: #ffffff;
    margin: 0 0 16px 0;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  p {
    color: #cfc9be;
    font-size: 1.1rem;
    line-height: 1.75;
    max-width: 650px;
    margin: 0 auto 36px auto;
  }
`;

const ActionRow = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
  flex-wrap: wrap;
`;

const PrimaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 15px 36px;
  background: linear-gradient(135deg, #e8c547 0%, #d4af37 100%);
  color: #0b0f19;
  font-weight: 700;
  font-size: 0.95rem;
  border-radius: 30px;
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 0 8px 24px rgba(212, 175, 55, 0.25);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 30px rgba(212, 175, 55, 0.4);
  }
`;

const SecondaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 15px 36px;
  background: transparent;
  color: #ffffff;
  font-weight: 600;
  font-size: 0.95rem;
  border-radius: 30px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: #ffffff;
    transform: translateY(-3px);
  }
`;

export default function AboutPage() {
  return (
    <Wrapper>
      {/* Hero Section */}
      <HeroSection>
        <div className="heroContent">
          <HeroTitle>About Jindal Group</HeroTitle>
          <HeroSubtitle>Building Trust, Reliability, and Business Excellence Since 1965</HeroSubtitle>
        </div>
      </HeroSection>

      <Container>
        {/* Company Overview Section */}
        <Section>
          <OverviewGrid>
            <OverviewText>
              <p>
                Founded in <span className="highlight">1965</span> by <span className="highlight">Shri Ramkaran H. Jindal</span>, 
                Jindal Group has grown into one of Central India's trusted names in tyre distribution, 
                petroleum transportation, and industrial fuel solutions.
              </p>
              <p>
                What began as a single Indian Oil Corporation dealership has evolved over six decades into a diversified 
                business group. Today, we serve key manufacturing and transportation hubs across Maharashtra, Madhya Pradesh, 
                and Chhattisgarh, powering industrial growth with integrity and operational excellence.
              </p>
            </OverviewText>
          </OverviewGrid>
        </Section>

        {/* Our Journey Timeline Section */}
        <Section>
          <SectionTitle>Our Journey</SectionTitle>
          <Timeline>
            <TimelineNode>
              <TimelineDot />
              <TimelineContent>
                <h3>1965</h3>
                <p>Established as an Indian Oil Corporation dealer, laying the foundation for trust and quality fuel service in the region.</p>
              </TimelineContent>
            </TimelineNode>

            <TimelineNode>
              <TimelineDot />
              <TimelineContent>
                <h3>1979</h3>
                <p>Expanded operations into a JK Tyre dealership, providing comprehensive tyre sales and premium support services.</p>
              </TimelineContent>
            </TimelineNode>

            <TimelineNode>
              <TimelineDot />
              <TimelineContent>
                <h3>Growth Phase</h3>
                <p>Diversified operations into bulk petroleum transportation with a growing fleet of tankers, and established industrial fuel trading services.</p>
              </TimelineContent>
            </TimelineNode>

            <TimelineNode>
              <TimelineDot />
              <TimelineContent>
                <h3>Today</h3>
                <p>Recognized as a leading multi-division enterprise and trusted partner for industrial fuels, transportation, and high-performance tyres across Central India.</p>
              </TimelineContent>
            </TimelineNode>
          </Timeline>
        </Section>

        {/* Our Strengths Section */}
        <Section>
          <SectionTitle>Our Strengths</SectionTitle>
          <StrengthsGrid>
            <StrengthCard>
              {/* Award / Medal SVG */}
              <svg viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H7c0-2.76 2.24-5 5-5s5 2.24 5 5c0 1.04-.42 1.99-1.07 2.25z"/>
              </svg>
              <div className="content">
                <h3>60+ Years Experience</h3>
                <p>A solid legacy of deep market expertise, stable leadership, and reliable industrial services since 1965.</p>
              </div>
            </StrengthCard>

            <StrengthCard>
              {/* Wheel/Tyre SVG */}
              <svg viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6zm0-10c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 6c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/>
              </svg>
              <div className="content">
                <h3>Leading JK Tyre & Bridgestone Dealer</h3>
                <p>Authorised distributor providing bulk supply, custom tyre sizes, and advanced service bay support.</p>
              </div>
            </StrengthCard>

            <StrengthCard>
              {/* Network / Logistics Truck SVG */}
              <svg viewBox="0 0 24 24">
                <path d="M20 8h-3V4H4c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm12 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm1.5-6.5H17V9h2.5l2 3H19.5z"/>
              </svg>
              <div className="content">
                <h3>Strong Transportation Network</h3>
                <p>A massive, modern fleet of tankers for safe, timely, and compliant transport of petroleum products.</p>
              </div>
            </StrengthCard>

            <StrengthCard>
              {/* Fuel Droplet SVG */}
              <svg viewBox="0 0 24 24">
                <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/>
              </svg>
              <div className="content">
                <h3>Reliable Fuel Supply</h3>
                <p>Direct supply partnerships with major public/private sector refineries for high-quality industrial fuel.</p>
              </div>
            </StrengthCard>

            <StrengthCard>
              {/* Team SVG */}
              <svg viewBox="0 0 24 24">
                <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
              </svg>
              <div className="content">
                <h3>Experienced Team</h3>
                <p>Dedicated transportation professionals, skilled technicians, and trading advisors supporting your operations.</p>
              </div>
            </StrengthCard>

            <StrengthCard>
              {/* Relationships / Handshake SVG */}
              <svg viewBox="0 0 24 24">
                <path d="M12.5 10.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm-5 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm5 4c-2.33 0-7 1.17-7 3.5V20h14v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
              </svg>
              <div className="content">
                <h3>Customer Relationships</h3>
                <p>Decades of long-term business partnerships built on integrity, transparent pricing, and mutual growth.</p>
              </div>
            </StrengthCard>
          </StrengthsGrid>
        </Section>

        {/* Group Companies Section */}
        <Section>
          <SectionTitle>Group Companies</SectionTitle>
          <CompanyGrid>
            <CompanyCard to="/rajesh-automobiles">
              <h3>Rajesh Automobiles</h3>
              <p>One of Central India's leading petroleum transporters with a large tanker fleet serving public and private sector refineries.</p>
              <span className="action-text">Explore Division</span>
            </CompanyCard>

            <CompanyCard to="/petroworld">
              <h3>Petroworld Servo</h3>
              <p>State-of-the-art Indian Oil retail outlet supplying quality fuel products, high-grade lubricants, and vehicle support.</p>
              <span className="action-text">Explore Division</span>
            </CompanyCard>

            <CompanyCard to="/naman-enterprises">
              <h3>Naman Enterprises</h3>
              <p>Bulk trading and supply of Furnace Oil, Light Diesel Oil (LDO), and alternative industrial fuels for manufacturing clients.</p>
              <span className="action-text">Explore Division</span>
            </CompanyCard>

            <CompanyCard to="/tyres">
              <h3>Jindal Tyres</h3>
              <p>Premium tyre dealership and wheel alignment center offering tyres for all vehicles, advanced 3D alignment, computerized balancing, and car pickup & drop facility.</p>
              <span className="action-text">Explore Division</span>
            </CompanyCard>

            <CompanyCard to="/minggo">
              <h3>MinGGo Store</h3>
              <p>Premium kids store specializing in quality toys, baby care essentials, school supplies, birthday gifts, and family items.</p>
              <span className="action-text">Explore Division</span>
            </CompanyCard>

            <CompanyCard to="/minggo-playzone">
              <h3>MinGGo Playzone</h3>
              <p>Nagpur’s premium indoor play area and party venue for kids. Featuring soft play areas, slides, ball pools, interactive games, and custom birthday packages.</p>
              <span className="action-text">Explore Division</span>
            </CompanyCard>
          </CompanyGrid>
        </Section>

        {/* Industries We Serve Section */}
        <Section>
          <SectionTitle>Industries We Serve</SectionTitle>
          <IndustryGrid>
            <IndustryCard>
              {/* Factory Icon */}
              <svg viewBox="0 0 24 24">
                <path d="M4 20h16v-2h-3v-3h3v-2h-3V10l-4 4V10L9 14V4L4 9v11z"/>
              </svg>
              <span>Manufacturing</span>
            </IndustryCard>

            <IndustryCard>
              {/* Flash / Power Icon */}
              <svg viewBox="0 0 24 24">
                <path d="M11.5 2L3 13h8v9l8.5-11h-8z"/>
              </svg>
              <span>Power Plants</span>
            </IndustryCard>

            <IndustryCard>
              {/* Truck Icon */}
              <svg viewBox="0 0 24 24">
                <path d="M20 8h-3V4H4c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm12 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
              </svg>
              <span>Transportation</span>
            </IndustryCard>

            <IndustryCard>
              {/* Automotive Icon */}
              <svg viewBox="0 0 24 24">
                <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.85 7h10.29l1.04 3H5.81l1.04-3zM6 16c-.83 0-1.5-.67-1.5-1.5S5.17 13 6 13s1.5.67 1.5 1.5S6.83 16 6 16zm12 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
              </svg>
              <span>Automotive</span>
            </IndustryCard>

            <IndustryCard>
              {/* Gear/Engineering Icon */}
              <svg viewBox="0 0 24 24">
                <path d="M19.43 12.98c.04-.32.07-.64.07-.98s-.03-.66-.07-.98l2.11-1.65c.19-.15.24-.42.12-.64l-2-3.46c-.12-.22-.39-.3-.61-.22l-2.49 1c-.52-.4-1.08-.73-1.69-.98l-.38-2.65C14.46 2.18 14.25 2 14 2h-4c-.25 0-.46.18-.49.42l-.38 2.65c-.61.25-1.17.59-1.69.98l-2.49-1c-.23-.09-.49 0-.61.22l-2 3.46c-.13.22-.07.49.12.64l2.11 1.65c-.04.32-.07.65-.07.98s.03.66.07.98l-2.11 1.65c-.19.15-.24.42-.12.64l2 3.46c.12.22.39.3.61.22l2.49-1c.52.4 1.08.73 1.69.98l.38 2.65c.03.24.24.42.49.42h4c.25 0 .46-.18.49-.42l.38-2.65c.61-.25 1.17-.59 1.69-.98l2.49 1c.23.09.49 0 .61-.22l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.65zM12 15.5c-1.93 0-3.5-1.57-3.5-3.5s1.57-3.5 3.5-3.5 3.5 1.57 3.5 3.5-1.57 3.5-3.5 3.5z"/>
              </svg>
              <span>Engineering Industries</span>
            </IndustryCard>
          </IndustryGrid>
        </Section>

        {/* Why Choose Us Section */}
        <Section>
          <SectionTitle>Why Choose Us</SectionTitle>
          <WhyGrid>
            <WhyItem>
              <div className="check">✓</div>
              <span>Legacy of Trust Since 1965</span>
            </WhyItem>
            <WhyItem>
              <div className="check">✓</div>
              <span>Reliable Supply Chain Operations</span>
            </WhyItem>
            <WhyItem>
              <div className="check">✓</div>
              <span>Industry Expertise</span>
            </WhyItem>
            <WhyItem>
              <div className="check">✓</div>
              <span>Customer-Focused Service</span>
            </WhyItem>
            <WhyItem>
              <div className="check">✓</div>
              <span>Proven Track Record</span>
            </WhyItem>
            <WhyItem>
              <div className="check">✓</div>
              <span>Strong Regional Presence</span>
            </WhyItem>
          </WhyGrid>
        </Section>

        {/* CTA Section */}
        <CTAWrapper>
          <h3>Looking for a Reliable Industrial Partner?</h3>
          <p>We are committed to delivering quality products, dependable transportation, and long-term business value for your enterprise.</p>
          <ActionRow>
            <PrimaryButton href="/#contact">Contact Us</PrimaryButton>
            <SecondaryButton href="tel:9970153000">Request a Quote</SecondaryButton>
          </ActionRow>
        </CTAWrapper>
      </Container>
    </Wrapper>
  );
}
